import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { UNALIGN_REASONS } from '../logic/cmsUnalignReasons';
import { useQueryClient } from '@tanstack/react-query';
import { useTriggerCmsUnalignment, useCmsSubmissionPoll, useCaptureCmsBaseline } from '../state/data/cms';
import { CmsResultCodes, freshTerminalRow } from '@/logic/cmsResultCodes';
import { scheduleInvalidate } from '@/state/data/_shared/wf-race';
import { TASK_KEYS } from '@/state/data/task/keys';
import { CMS_KEYS } from '@/state/data/cms/keys';
import { CARE_PERIOD_KEYS } from '@/state/data/carePeriod/keys';
import { LoadingPanel, VerdictBanner, Section, Callout } from './cms/cmsModalParts';
import { CLINICAL_EXCLUSION_CONDITIONS } from '@/logic/cmsExclusionConditions';

// CMS requires ≥1 clinical-exclusion condition when the unalignment reason is this
// (constraint access-unalign-condition-required). Only this reason shows the picker.
const CONDITIONS_REQUIRED_REASON = 'no-longer-clinically-eligible';

// CmsUnalignmentModal — confirm → fire-and-poll verdict (parity with the
// eligibility/alignment modals). apiv2 acks unalignment_requested immediately but
// the CMS $unalign verdict lands on the cms_submissions row ~10-24s later, so the
// modal polls that row rather than the event response — which is what surfaces
// `cannot-unalign-during-lock-in` (previously a silent no-op).

// Cap mirrors the eligibility + alignment modals' 30s.
const POLL_CAP_MS = 30_000;
const EMPTY_IDS = new Set();

const SUBMIT_LOADING_STEPS = [
  { at: 0,    label: 'Authenticating with CMS' },
  { at: 1500, label: 'Submitting to /Patient/$unalign' },
  { at: 6000, label: 'Reviewing CMS response' },
];

// Opened from a withdrawal request, the window shows the request's reason as text and makes the
// coordinator pick the CMS reason: a request is writable from outside the app, so it never pre-fills one.
export default function CmsUnalignmentModal({
  patient, carePeriod, onClose, onSubmitted, onUnaligned = null, requestReasonLabel = null,
}) {
  // 'confirm'    → reason select + confirm (default)
  // 'submitting' → event fired, polling cms_submissions(operation='unalign')
  // 'result'     → terminal verdict rendered
  const [phase, setPhase] = useState('confirm');
  const fromRequest = requestReasonLabel != null;
  const [reason, setReason] = useState(fromRequest ? '' : 'patient-initiated');
  // Selected clinical-exclusion ICD-10 codes (only used when reason requires them).
  const [conditionCodes, setConditionCodes] = useState([]);
  const [result, setResult] = useState(null);
  const [resultVisible, setResultVisible] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [submitError, setSubmitError] = useState(null);
  // null until captured — gates the poll on (phase submitting AND captured).
  const [baselineIds, setBaselineIds] = useState(null);
  const aliveRef = useRef(true);
  const capTimerRef = useRef(null);
  const settledRef = useRef(false); // verdict OR cap resolved — keeps both paths idempotent

  const qc = useQueryClient();
  const triggerMut = useTriggerCmsUnalignment();
  const captureBaseline = useCaptureCmsBaseline('unalign');
  const uid = String(patient.userid ?? '');
  const poll = useCmsSubmissionPoll('unalign', uid, {
    enabled: phase === 'submitting' && !!baselineIds,
    baselineIds: baselineIds || EMPTY_IDS,
  });

  const inFlight = phase === 'submitting';
  const conditionsRequired = reason === CONDITIONS_REQUIRED_REASON;
  const conditionsMissing = conditionsRequired && conditionCodes.length === 0;

  const toggleCondition = useCallback((code) => {
    setConditionCodes(prev => (prev.includes(code) ? prev.filter(c => c !== code) : [...prev, code]));
  }, []);

  // Build the verdict `result` the render expects from a terminal poll row.
  const rowToResult = useCallback((row) => ({
    result_code: row.result_code,
    display: row.result_display,
    reason: row.result_display,
    checked_at: row.finished_at || row.created || row.created_at || null,
  }), []);

  function handleSubmit() {
    if (inFlight || !carePeriod || !reason || conditionsMissing) return;
    setSubmitError(null);
    setStepIndex(0);
    setResult(null);
    setResultVisible(false);
    settledRef.current = false;
    setPhase('submitting');
    (async () => {
      // Snapshot existing unalign rows BEFORE firing — skew-immune freshness.
      const baseline = await captureBaseline(uid);
      if (!aliveRef.current) return;
      setBaselineIds(baseline); // enables the poll (gated on phase + captured)
      if (capTimerRef.current) clearTimeout(capTimerRef.current);
      capTimerRef.current = setTimeout(() => {
        // Fail-soft: CMS slower than the cap → pending ("care team will follow up").
        if (!aliveRef.current || settledRef.current) return;
        settledRef.current = true;
        setResult({ result_code: 'pending' });
        setPhase('result');
        requestAnimationFrame(() => { if (aliveRef.current) setResultVisible(true); });
      }, POLL_CAP_MS);
      // Fire; the verdict comes from the poll, not this mutation's response.
      triggerMut.mutate(
        {
          withingsUserId: patient.userid,
          carePeriodId: carePeriod.care_period_id,
          reason,
          // ICD-10 codes only when the reason requires them; WF24 re-derives the
          // canonical display from the code (client display is never sent to CMS).
          conditions: conditionsRequired ? conditionCodes : [],
        },
        {
          onError: (e) => {
            // The fire itself failed (network/transport) — back to confirm.
            if (!aliveRef.current || settledRef.current) return;
            settledRef.current = true;
            if (capTimerRef.current) clearTimeout(capTimerRef.current);
            setSubmitError(e.message || 'Unalignment failed');
            setPhase('confirm');
          },
        },
      );
    })();
  }

  // Verdict from the poll: the first FRESH row (correlation_id not in baselineIds)
  // that has reached a terminal verdict. Same predicate the hook's refetchInterval uses.
  useEffect(() => {
    if (phase !== 'submitting' || !baselineIds) return;
    const fresh = freshTerminalRow(poll.data, baselineIds, 'unalign');
    if (!fresh || settledRef.current) return;
    settledRef.current = true;
    if (capTimerRef.current) clearTimeout(capTimerRef.current);
    setResult(rowToResult(fresh));
    setPhase('result');
    requestAnimationFrame(() => { if (aliveRef.current) setResultVisible(true); });
    // WF24 care-period PATCH (unaligned / pending_unalignment) lands out-of-band —
    // reconcile past the SLA. (A lock-in / not-aligned verdict leaves the period
    // unchanged, so the refetch is a harmless no-op.)
    scheduleInvalidate(qc, TASK_KEYS.all);
    scheduleInvalidate(qc, CMS_KEYS.all);
    scheduleInvalidate(qc, CARE_PERIOD_KEYS.byUser(uid));
  }, [phase, baselineIds, poll.data, rowToResult, qc, uid]);

  // Loading-step animation
  useEffect(() => {
    if (phase !== 'submitting') return undefined;
    const timers = SUBMIT_LOADING_STEPS.map((s, i) =>
      setTimeout(() => { if (aliveRef.current) setStepIndex(i); }, s.at)
    );
    return () => timers.forEach(clearTimeout);
  }, [phase]);

  useEffect(() => {
    aliveRef.current = true;
    return () => { aliveRef.current = false; if (capTimerRef.current) clearTimeout(capTimerRef.current); };
  }, []);

  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prevOverflow; };
  }, []);

  // Capture + stopPropagation: the patient drawer's own Escape listener would close it too.
  // Gated off mid-submit so a stray key can't dismiss the in-flight wait.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Escape' || inFlight) return;
      e.stopPropagation();
      onClose();
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [onClose, inFlight]);

  const resultMeta = result
    ? CmsResultCodes.labelFor(result.result_code) || {
        headline: result.display || 'Unalignment result',
        explainer: '',
        tone: 'moderate',
      }
    : null;
  const isUnaligned = result?.result_code === 'unaligned';
  // A verdict that changed (or is changing) the care period → "Done" (refetch).
  // A rejection (lock-in, not-aligned, unknown, transport error) left the period
  // unchanged → "Close" only, so the shell's "unalignment submitted" toast + refetch
  // never fire on a request CMS refused.
  const isPendingish = result ? ['pending', 'unalignment-pending'].includes(result.result_code) : false;
  const patientName = [
    patient.firstname || patient.first_name,
    patient.lastname || patient.last_name,
  ].filter(Boolean).join(' ').trim();

  function handleDone() {
    if (isUnaligned && onUnaligned) onUnaligned();
    else onSubmitted?.();
  }

  return createPortal((
    <div className="modal-overlay" onClick={() => { if (!inFlight) onClose(); }}>
      <div
        className="modal-content"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 560, width: '100%', padding: 0, display: 'flex', flexDirection: 'column', maxHeight: '88vh' }}
      >
        {/* Header */}
        <div style={{
          padding: '20px 24px 16px',
          borderBottom: '1px solid var(--wi-divider)',
          display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
          gap: 12, flexShrink: 0,
        }}>
          <div style={{ minWidth: 0 }}>
            <h2 className="header3" style={{ margin: 0 }}>CMS Unalignment</h2>
            <p className="detail1" style={{ margin: '4px 0 0', color: 'var(--wi-text-secondary)' }}>
              Medicare $unalign · ACCESS Model{patientName && <> · {patientName}</>}
            </p>
          </div>
          <button className="modal-close" aria-label="Close" onClick={onClose} disabled={inFlight} style={{ position: 'static' }}>×</button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>
          {phase === 'confirm' && (
            <div>
              <Callout tone="moderate">
                <p className="body2" style={{ margin: 0, color: 'var(--wi-text-primary-moderate)' }}>
                  This requests CMS to unalign the patient from the ACCESS Model. On success the care period is closed and the patient is disenrolled. CMS may reject the request — for example during the mandatory lock-in period — and you&apos;ll see the verdict here.
                </p>
              </Callout>
              {fromRequest && (
                <p className="body2" style={{ margin: '16px 0 0', color: 'var(--wi-text-primary)' }}>
                  {`The withdrawal request says: ${requestReasonLabel}`}
                </p>
              )}
              <Section label="Reason for unalignment">
                <select
                  value={reason}
                  onChange={e => { setReason(e.target.value); setConditionCodes([]); }}
                  aria-label="Reason for unalignment"
                  style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid var(--wi-divider)', background: 'var(--wi-background-primary)', color: 'var(--wi-text-primary)', fontSize: 14, fontFamily: 'var(--wi-font-family)', width: '100%', outline: 'none' }}
                >
                  {fromRequest && <option value="" disabled>Choose a reason</option>}
                  {UNALIGN_REASONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                </select>
              </Section>
              {conditionsRequired && (
                <Section label="Disqualifying diagnosis (required — select at least one)">
                  <div role="group" aria-label="Disqualifying diagnosis" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {CLINICAL_EXCLUSION_CONDITIONS.map(c => (
                      <label key={c.code} className="body2" style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', color: 'var(--wi-text-primary)' }}>
                        <input
                          type="checkbox"
                          checked={conditionCodes.includes(c.code)}
                          onChange={() => toggleCondition(c.code)}
                        />
                        <span>{c.label} <span className="detail1" style={{ color: 'var(--wi-text-placeholder)', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace' }}>{c.code}</span></span>
                      </label>
                    ))}
                  </div>
                  <p className="detail1" style={{ margin: '8px 0 0', color: 'var(--wi-text-secondary)' }}>
                    CMS requires a documented disqualifying diagnosis for this reason.
                  </p>
                </Section>
              )}
              {submitError && (
                <p className="detail1" style={{ margin: '12px 0 0', color: 'var(--wi-status-bad)' }}>{submitError}</p>
              )}
            </div>
          )}

          {phase === 'submitting' && (
            <LoadingPanel
              title="Submitting unalignment to CMS…"
              stepIndex={stepIndex}
              steps={SUBMIT_LOADING_STEPS.map(s => s.label)}
              footnote="CMS may take up to 30 seconds to respond."
            />
          )}

          {phase === 'result' && result && resultMeta && (
            <div style={{ opacity: resultVisible ? 1 : 0, transition: 'opacity 200ms ease' }}>
              <VerdictBanner
                tone={resultMeta.tone}
                headline={resultMeta.headline}
                resultCode={result.result_code}
                fhirDisplay={result.display}
                explainer={resultMeta.explainer}
              />
              {isUnaligned && (
                <Callout tone="good">
                  <p className="body2" style={{ margin: 0, color: 'var(--wi-text-primary-good)' }}>
                    The care period is being closed. Click Done to refresh.
                  </p>
                </Callout>
              )}
              {result.checked_at && (
                <p className="detail1" style={{ margin: '20px 0 0', color: 'var(--wi-text-placeholder)' }}>
                  Recorded {new Date(result.checked_at).toLocaleString()}
                </p>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          display: 'flex', gap: 10, justifyContent: 'flex-end', alignItems: 'center',
          padding: '14px 24px', borderTop: '1px solid var(--wi-divider)', flexShrink: 0,
        }}>
          {phase === 'confirm' && (
            <>
              <button className="btn-secondary" onClick={onClose}>Cancel</button>
              <button className="btn-primary" onClick={handleSubmit} disabled={!carePeriod || !reason || conditionsMissing}>Submit unalignment</button>
            </>
          )}
          {phase === 'submitting' && (
            <button className="btn-secondary" onClick={onClose}>Cancel</button>
          )}
          {phase === 'result' && (
            (isUnaligned || isPendingish)
              ? <button className="btn-primary" onClick={handleDone}>Done</button>
              : <button className="btn-primary" onClick={onClose}>Close</button>
          )}
        </div>
      </div>
    </div>
  ), document.body);
}
